'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Loader2, UserPlus, ClipboardCheck, Users, Building2, BadgeCheck, CheckCircle2, ChevronRight, Plus, Pencil, Trash2, Upload, X, Star } from 'lucide-react';
import { toast } from 'sonner';
import {
  onboardingService,
  type NewHireOnboarding,
  type OnboardingWithDetails,
  type OnboardingCredential,
  type OnboardingEmergencyContact,
  type HireType,
  type OnboardingStatus,
} from '@/lib/services/staffingService';
import Icon from '@/components/ui/AppIcon';


// ─── Step Config ──────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Basic Info', icon: UserPlus, description: 'Personal & contact details' },
  { id: 2, label: 'Credentials', icon: BadgeCheck, description: 'Upload licenses & certs' },
  { id: 3, label: 'Emergency Contact', icon: Users, description: 'Emergency contact info' },
  { id: 4, label: 'Facility & Role', icon: Building2, description: 'Assignment details' },
  { id: 5, label: 'Classification', icon: ClipboardCheck, description: 'Direct or temp hire' },
];

const ROLES = ['RN', 'CNA', 'LPN', 'NP', 'MD', 'PA', 'PT', 'OT', 'RT', 'Pharmacist'];
const SPECIALTIES = ['ICU', 'ER', 'Pediatrics', 'Oncology', 'General', 'Surgery', 'Cardiology', 'Neurology', 'Orthopedics', 'Radiology'];
const FACILITIES = ['Sunrise Medical Center', 'Riverside Health Clinic', 'Valley Children Hospital', 'Metro General Hospital', 'Lakeside Surgical Center'];
const DEPARTMENTS = ['Critical Care', 'Emergency', 'Nursing', 'Pediatrics', 'Surgery', 'Cardiology', 'Pharmacy', 'Radiology'];
const CREDENTIAL_TYPES = ['license', 'certification', 'training', 'background'];
const RELATIONSHIPS = ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'];

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: OnboardingStatus }) {
  const map: Record<OnboardingStatus, { label: string; cls: string }> = {
    in_progress: { label: 'In Progress', cls: 'bg-warning/10 text-warning border-warning/20' },
    completed: { label: 'Completed', cls: 'bg-success/10 text-success border-success/20' },
    pending_review: { label: 'Pending Review', cls: 'bg-primary/10 text-primary border-primary/20' },
  };
  const { label, cls } = map[status] ?? map.in_progress;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-600 border ${cls}`}>
      {label}
    </span>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep, completedStep }: { currentStep: number; completedStep: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((step, idx) => {
        const done = step.id <= completedStep;
        const active = step.id === currentStep;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                  done
                    ? 'bg-primary border-primary text-primary-foreground'
                    : active
                    ? 'bg-primary/10 border-primary text-primary' :'bg-muted border-border text-muted-foreground'
                }`}
              >
                {done && step.id < currentStep ? (
                  <CheckCircle2 size={16} />
                ) : (
                  <Icon size={15} />
                )}
              </div>
              <span className={`text-[10px] font-600 text-center leading-tight ${active ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-5 mx-1 transition-all ${step.id < currentStep ? 'bg-primary' : 'bg-border'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Basic Info ───────────────────────────────────────────────────────

interface BasicInfoForm {
  fullName: string; email: string; phone: string; startDate: string; notes: string;
}

function Step1BasicInfo({ data, onChange }: { data: BasicInfoForm; onChange: (d: BasicInfoForm) => void }) {
  const set = (k: keyof BasicInfoForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...data, [k]: e.target.value });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Full Name <span className="text-danger">*</span></label>
          <input value={data.fullName} onChange={set('fullName')} placeholder="e.g. Sarah Mitchell"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Email Address <span className="text-danger">*</span></label>
          <input type="email" value={data.email} onChange={set('email')} placeholder="name@example.com"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Phone Number</label>
          <input value={data.phone} onChange={set('phone')} placeholder="(555) 000-0000"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Expected Start Date</label>
          <input type="date" value={data.startDate} onChange={set('startDate')}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-600 text-foreground mb-1.5">Notes</label>
        <textarea value={data.notes} onChange={set('notes')} rows={3} placeholder="Any additional notes about this hire..."
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none" />
      </div>
    </div>
  );
}

// ─── Step 2: Credentials ──────────────────────────────────────────────────────

interface CredentialForm {
  credentialType: string; credentialName: string; issuingBody: string;
  licenseNumber: string; issueDate: string; expiryDate: string; fileName: string; notes: string;
}

const EMPTY_CRED: CredentialForm = { credentialType: 'license', credentialName: '', issuingBody: '', licenseNumber: '', issueDate: '', expiryDate: '', fileName: '', notes: '' };

function Step2Credentials({
  credentials, onAdd, onDelete,
}: {
  credentials: OnboardingCredential[];
  onAdd: (c: CredentialForm) => void;
  onDelete: (id: string) => void;
}) {
  const [form, setForm] = useState<CredentialForm>(EMPTY_CRED);
  const [adding, setAdding] = useState(false);
  const set = (k: keyof CredentialForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleAdd = () => {
    if (!form.credentialName.trim()) { toast.error('Credential name is required'); return; }
    onAdd(form);
    setForm(EMPTY_CRED);
    setAdding(false);
  };

  const statusColor: Record<string, string> = {
    pending: 'bg-muted text-muted-foreground',
    uploaded: 'bg-primary/10 text-primary',
    verified: 'bg-success/10 text-success',
    rejected: 'bg-danger/10 text-danger',
  };

  return (
    <div className="space-y-4">
      {credentials.length === 0 && !adding && (
        <div className="border border-dashed border-border rounded-xl p-8 text-center">
          <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No credentials added yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add licenses, certifications, and training documents</p>
        </div>
      )}

      {credentials.map((c) => (
        <div key={c.id} className="flex items-start gap-3 p-3.5 bg-muted/40 border border-border rounded-xl">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-600 text-foreground">{c.credentialName}</span>
              <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full capitalize ${statusColor[c.uploadStatus] ?? statusColor.pending}`}>
                {c.uploadStatus}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{c.issuingBody} {c.licenseNumber ? `· #${c.licenseNumber}` : ''}</p>
            {c.expiryDate && <p className="text-xs text-muted-foreground">Expires: {c.expiryDate}</p>}
            {c.fileName && <p className="text-xs text-primary mt-0.5 flex items-center gap-1"><Upload size={10} />{c.fileName}</p>}
          </div>
          <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors flex-shrink-0">
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {adding ? (
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3">
          <p className="text-sm font-600 text-foreground">Add Credential</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Type</label>
              <select value={form.credentialType} onChange={set('credentialType')}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize">
                {CREDENTIAL_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Credential Name <span className="text-danger">*</span></label>
              <input value={form.credentialName} onChange={set('credentialName')} placeholder="e.g. RN State License"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Issuing Body</label>
              <input value={form.issuingBody} onChange={set('issuingBody')} placeholder="e.g. California BRN"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">License / Cert Number</label>
              <input value={form.licenseNumber} onChange={set('licenseNumber')} placeholder="e.g. RN-CA-884521"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Issue Date</label>
              <input type="date" value={form.issueDate} onChange={set('issueDate')}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Expiry Date</label>
              <input type="date" value={form.expiryDate} onChange={set('expiryDate')}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-600 text-foreground mb-1">File Name (simulated upload)</label>
              <input value={form.fileName} onChange={set('fileName')} placeholder="e.g. rn_license.pdf"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleAdd} className="px-4 py-1.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 transition-colors">
              Add Credential
            </button>
            <button onClick={() => { setAdding(false); setForm(EMPTY_CRED); }}
              className="px-4 py-1.5 bg-muted text-foreground text-sm font-600 rounded-lg hover:bg-muted/80 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-4 py-2 border border-dashed border-primary/40 text-primary text-sm font-600 rounded-xl hover:bg-primary/5 transition-colors w-full justify-center">
          <Plus size={15} /> Add Credential
        </button>
      )}
    </div>
  );
}

// ─── Step 3: Emergency Contacts ───────────────────────────────────────────────

interface ContactForm {
  contactName: string; relationship: string; phonePrimary: string;
  phoneSecondary: string; email: string; address: string; isPrimary: boolean;
}

const EMPTY_CONTACT: ContactForm = { contactName: '', relationship: 'Spouse', phonePrimary: '', phoneSecondary: '', email: '', address: '', isPrimary: false };

function Step3EmergencyContacts({
  contacts, onAdd, onDelete,
}: {
  contacts: OnboardingEmergencyContact[];
  onAdd: (c: ContactForm) => void;
  onDelete: (id: string) => void;
}) {
  const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT);
  const [adding, setAdding] = useState(false);
  const set = (k: keyof ContactForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleAdd = () => {
    if (!form.contactName.trim()) { toast.error('Contact name is required'); return; }
    if (!form.phonePrimary.trim()) { toast.error('Primary phone is required'); return; }
    onAdd(form);
    setForm(EMPTY_CONTACT);
    setAdding(false);
  };

  return (
    <div className="space-y-4">
      {contacts.length === 0 && !adding && (
        <div className="border border-dashed border-border rounded-xl p-8 text-center">
          <Users size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No emergency contacts added</p>
          <p className="text-xs text-muted-foreground mt-1">Add at least one emergency contact for compliance</p>
        </div>
      )}

      {contacts.map((c) => (
        <div key={c.id} className="flex items-start gap-3 p-3.5 bg-muted/40 border border-border rounded-xl">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-600 text-foreground">{c.contactName}</span>
              {c.isPrimary && <span className="text-[10px] font-600 px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1"><Star size={9} />Primary</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{c.relationship} · {c.phonePrimary}</p>
            {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
            {c.address && <p className="text-xs text-muted-foreground">{c.address}</p>}
          </div>
          <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors flex-shrink-0">
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {adding ? (
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3">
          <p className="text-sm font-600 text-foreground">Add Emergency Contact</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Full Name <span className="text-danger">*</span></label>
              <input value={form.contactName} onChange={set('contactName')} placeholder="Contact full name"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Relationship</label>
              <select value={form.relationship} onChange={set('relationship')}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Primary Phone <span className="text-danger">*</span></label>
              <input value={form.phonePrimary} onChange={set('phonePrimary')} placeholder="(555) 000-0000"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Secondary Phone</label>
              <input value={form.phoneSecondary} onChange={set('phoneSecondary')} placeholder="(555) 000-0000"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Email</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="contact@example.com"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Address</label>
              <input value={form.address} onChange={set('address')} placeholder="Street, City, State ZIP"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" id="isPrimary" checked={form.isPrimary}
                onChange={(e) => setForm((p) => ({ ...p, isPrimary: e.target.checked }))}
                className="w-4 h-4 rounded border-border accent-primary" />
              <label htmlFor="isPrimary" className="text-xs font-600 text-foreground cursor-pointer">Mark as primary contact</label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={handleAdd} className="px-4 py-1.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 transition-colors">
              Add Contact
            </button>
            <button onClick={() => { setAdding(false); setForm(EMPTY_CONTACT); }}
              className="px-4 py-1.5 bg-muted text-foreground text-sm font-600 rounded-lg hover:bg-muted/80 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-4 py-2 border border-dashed border-primary/40 text-primary text-sm font-600 rounded-xl hover:bg-primary/5 transition-colors w-full justify-center">
          <Plus size={15} /> Add Emergency Contact
        </button>
      )}
    </div>
  );
}

// ─── Step 4: Facility & Role ──────────────────────────────────────────────────

interface FacilityRoleForm { role: string; specialty: string; facility: string; department: string; }

function Step4FacilityRole({ data, onChange }: { data: FacilityRoleForm; onChange: (d: FacilityRoleForm) => void }) {
  const set = (k: keyof FacilityRoleForm) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChange({ ...data, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {([
        { key: 'role', label: 'Clinical Role', options: ROLES },
        { key: 'specialty', label: 'Specialty', options: SPECIALTIES },
        { key: 'facility', label: 'Facility', options: FACILITIES },
        { key: 'department', label: 'Department', options: DEPARTMENTS },
      ] as { key: keyof FacilityRoleForm; label: string; options: string[] }[]).map(({ key, label, options }) => (
        <div key={key}>
          <label className="block text-xs font-600 text-foreground mb-1.5">{label} <span className="text-danger">*</span></label>
          <select value={data[key]} onChange={set(key)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors">
            <option value="">Select {label}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

// ─── Step 5: Classification ───────────────────────────────────────────────────

interface ClassificationForm { hireType: HireType; agencyName: string; }

function Step5Classification({ data, onChange }: { data: ClassificationForm; onChange: (d: ClassificationForm) => void }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Select the employment classification for this new hire.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(['direct', 'temp'] as HireType[]).map((type) => (
          <button
            key={type}
            onClick={() => onChange({ ...data, hireType: type })}
            className={`p-5 rounded-xl border-2 text-left transition-all ${
              data.hireType === type
                ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/40 hover:bg-muted/40'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-3 ${data.hireType === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {type === 'direct' ? <BadgeCheck size={16} /> : <Building2 size={16} />}
            </div>
            <p className="text-sm font-700 text-foreground capitalize">{type === 'direct' ? 'Direct Hire' : 'Temp / Agency Hire'}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {type === 'direct' ?'Employee hired directly by the facility with full benefits and permanent status.' :'Placed through a staffing agency on a temporary or contract basis.'}
            </p>
          </button>
        ))}
      </div>
      {data.hireType === 'temp' && (
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Agency Name</label>
          <input
            value={data.agencyName}
            onChange={(e) => onChange({ ...data, agencyName: e.target.value })}
            placeholder="e.g. MedPro Staffing"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
      )}
    </div>
  );
}

// ─── Onboarding Wizard Modal ──────────────────────────────────────────────────

interface WizardProps {
  existing?: OnboardingWithDetails | null;
  onClose: () => void;
  onSaved: (hire: NewHireOnboarding) => void;
}

function OnboardingWizard({ existing, onClose, onSaved }: WizardProps) {
  const [step, setStep] = useState(existing ? Math.min(existing.currentStep, 5) : 1);
  const [saving, setSaving] = useState(false);
  const [hireId, setHireId] = useState<string | null>(existing?.id ?? null);
  const [credentials, setCredentials] = useState<OnboardingCredential[]>(existing?.credentials ?? []);
  const [contacts, setContacts] = useState<OnboardingEmergencyContact[]>(existing?.emergencyContacts ?? []);

  const [basicInfo, setBasicInfo] = useState<BasicInfoForm>({
    fullName: existing?.fullName ?? '',
    email: existing?.email ?? '',
    phone: existing?.phone ?? '',
    startDate: existing?.startDate ?? '',
    notes: existing?.notes ?? '',
  });
  const [facilityRole, setFacilityRole] = useState<FacilityRoleForm>({
    role: existing?.role ?? '',
    specialty: existing?.specialty ?? '',
    facility: existing?.facility ?? '',
    department: existing?.department ?? '',
  });
  const [classification, setClassification] = useState<ClassificationForm>({
    hireType: existing?.hireType ?? 'direct',
    agencyName: existing?.agencyName ?? '',
  });

  const saveStep = async (targetStep: number) => {
    setSaving(true);
    try {
      const payload = {
        fullName: basicInfo.fullName,
        email: basicInfo.email,
        phone: basicInfo.phone,
        startDate: basicInfo.startDate || null,
        notes: basicInfo.notes,
        role: facilityRole.role,
        specialty: facilityRole.specialty,
        facility: facilityRole.facility,
        department: facilityRole.department,
        hireType: classification.hireType,
        agencyName: classification.agencyName,
        currentStep: targetStep,
        onboardingStatus: (targetStep > 5 ? 'completed' : 'in_progress') as OnboardingStatus,
      };

      let saved: NewHireOnboarding | null;
      if (hireId) {
        saved = await onboardingService.update(hireId, payload);
      } else {
        saved = await onboardingService.create(payload);
        if (saved) setHireId(saved.id);
      }
      if (saved) {
        onSaved(saved);
        return saved;
      }
    } catch {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
    return null;
  };

  const handleNext = async () => {
    if (step === 1 && !basicInfo.fullName.trim()) { toast.error('Full name is required'); return; }
    if (step === 1 && !basicInfo.email.trim()) { toast.error('Email is required'); return; }
    if (step === 4 && !facilityRole.role) { toast.error('Please select a role'); return; }
    if (step === 4 && !facilityRole.facility) { toast.error('Please select a facility'); return; }

    const nextStep = step + 1;
    let saved = await saveStep(nextStep);
    if (saved) setStep(nextStep);
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleFinish = async () => {
    let saved = await saveStep(6);
    if (saved) {
      toast.success('New hire onboarding completed!');
      onClose();
    }
  };

  const handleAddCredential = async (form: CredentialForm) => {
    if (!hireId) {
      let saved = await saveStep(step);
      if (!saved) return;
    }
    try {
      const created = await onboardingService.addCredential({
        onboardingId: hireId!,
        credentialType: form.credentialType,
        credentialName: form.credentialName,
        issuingBody: form.issuingBody,
        licenseNumber: form.licenseNumber,
        issueDate: form.issueDate || null,
        expiryDate: form.expiryDate || null,
        fileName: form.fileName,
        fileUrl: '',
        uploadStatus: form.fileName ? 'uploaded' : 'pending',
        notes: form.notes,
      });
      if (created) setCredentials((p) => [...p, created]);
      toast.success('Credential added');
    } catch { toast.error('Failed to add credential'); }
  };

  const handleDeleteCredential = async (id: string) => {
    try {
      await onboardingService.deleteCredential(id);
      setCredentials((p) => p.filter((c) => c.id !== id));
      toast.success('Credential removed');
    } catch { toast.error('Failed to remove credential'); }
  };

  const handleAddContact = async (form: ContactForm) => {
    if (!hireId) {
      let saved = await saveStep(step);
      if (!saved) return;
    }
    try {
      const created = await onboardingService.addEmergencyContact({
        onboardingId: hireId!,
        contactName: form.contactName,
        relationship: form.relationship,
        phonePrimary: form.phonePrimary,
        phoneSecondary: form.phoneSecondary,
        email: form.email,
        address: form.address,
        isPrimary: form.isPrimary,
      });
      if (created) setContacts((p) => [...p, created]);
      toast.success('Emergency contact added');
    } catch { toast.error('Failed to add contact'); }
  };

  const handleDeleteContact = async (id: string) => {
    try {
      await onboardingService.deleteEmergencyContact(id);
      setContacts((p) => p.filter((c) => c.id !== id));
      toast.success('Contact removed');
    } catch { toast.error('Failed to remove contact'); }
  };

  const stepTitles = ['Personal Information', 'Credential Upload', 'Emergency Contact', 'Facility & Role Assignment', 'Hire Classification'];
  const stepDescs = [
    'Enter the new hire\'s basic personal and contact information.',
    'Upload required licenses, certifications, and training documents.',
    'Provide emergency contact details for compliance records.',
    'Assign the hire to a facility, department, and clinical role.',
    'Classify this hire as direct or temporary/agency placement.',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-700 text-foreground">{stepTitles[step - 1]}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{stepDescs[step - 1]}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-5 flex-shrink-0">
          <StepIndicator currentStep={step} completedStep={step - 1} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {step === 1 && <Step1BasicInfo data={basicInfo} onChange={setBasicInfo} />}
          {step === 2 && <Step2Credentials credentials={credentials} onAdd={handleAddCredential} onDelete={handleDeleteCredential} />}
          {step === 3 && <Step3EmergencyContacts contacts={contacts} onAdd={handleAddContact} onDelete={handleDeleteContact} />}
          {step === 4 && <Step4FacilityRole data={facilityRole} onChange={setFacilityRole} />}
          {step === 5 && <Step5Classification data={classification} onChange={setClassification} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
          <button onClick={handleBack} disabled={step === 1}
            className="px-4 py-2 text-sm font-600 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Step {step} of {STEPS.length}</span>
            {step < STEPS.length ? (
              <button onClick={handleNext} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                Save & Continue <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={handleFinish} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-success text-white text-sm font-600 rounded-lg hover:bg-success/90 disabled:opacity-60 transition-colors">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Complete Onboarding
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Hire Card ────────────────────────────────────────────────────────────────

function HireCard({
  hire, onEdit, onDelete,
}: {
  hire: NewHireOnboarding;
  onEdit: (h: NewHireOnboarding) => void;
  onDelete: (id: string) => void;
}) {
  const initials = hire.fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const progress = Math.round(((hire.currentStep - 1) / STEPS.length) * 100);

  return (
    <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 hover:shadow-sm transition-all group">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-700 flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-600 text-foreground truncate">{hire.fullName}</p>
              <p className="text-xs text-muted-foreground">{hire.role || '—'} · {hire.facility || 'No facility'}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => onEdit(hire)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <Pencil size={13} />
              </button>
              <button onClick={() => onDelete(hire.id)} className="p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <StatusBadge status={hire.onboardingStatus} />
            <span className="text-[10px] text-muted-foreground capitalize">{hire.hireType} hire</span>
          </div>
          <div className="mt-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-muted-foreground">
                Step {Math.min(hire.currentStep, STEPS.length)} of {STEPS.length} — {STEPS[Math.min(hire.currentStep - 1, STEPS.length - 1)]?.label}
              </span>
              <span className="text-[10px] font-600 text-foreground">{progress}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          {hire.startDate && (
            <p className="text-[10px] text-muted-foreground mt-1.5">Start: {hire.startDate}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const [hires, setHires] = useState<NewHireOnboarding[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingHire, setEditingHire] = useState<OnboardingWithDetails | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [filterStatus, setFilterStatus] = useState<OnboardingStatus | 'all'>('all');
  const [filterHireType, setFilterHireType] = useState<HireType | 'all'>('all');
  const [search, setSearch] = useState('');

  const loadHires = useCallback(async () => {
    try {
      const data = await onboardingService.getAll();
      setHires(data);
    } catch {
      toast.error('Failed to load onboarding records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHires(); }, [loadHires]);

  const handleEdit = async (hire: NewHireOnboarding) => {
    setLoadingEdit(true);
    try {
      const details = await onboardingService.getById(hire.id);
      setEditingHire(details);
      setWizardOpen(true);
    } catch {
      toast.error('Failed to load hire details');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onboardingService.delete(id);
      setHires((p) => p.filter((h) => h.id !== id));
      toast.success('Onboarding record deleted');
    } catch {
      toast.error('Failed to delete record');
    }
  };

  const handleSaved = (hire: NewHireOnboarding) => {
    setHires((p) => {
      const idx = p.findIndex((h) => h.id === hire.id);
      if (idx >= 0) { const n = [...p]; n[idx] = hire; return n; }
      return [hire, ...p];
    });
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setEditingHire(null);
    loadHires();
  };

  const filtered = hires.filter((h) => {
    const matchSearch = !search || h.fullName.toLowerCase().includes(search.toLowerCase()) || h.email.toLowerCase().includes(search.toLowerCase()) || h.role.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || h.onboardingStatus === filterStatus;
    const matchType = filterHireType === 'all' || h.hireType === filterHireType;
    return matchSearch && matchStatus && matchType;
  });

  const stats = {
    total: hires.length,
    inProgress: hires.filter((h) => h.onboardingStatus === 'in_progress').length,
    completed: hires.filter((h) => h.onboardingStatus === 'completed').length,
    pendingReview: hires.filter((h) => h.onboardingStatus === 'pending_review').length,
  };

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-700 text-foreground">New Hire Onboarding</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Guided onboarding flow for credentials, contacts, facility assignment, and hire classification</p>
        </div>
        <button
          onClick={() => { setEditingHire(null); setWizardOpen(true); }}
          disabled={loadingEdit}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {loadingEdit ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
          Start Onboarding
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Hires', value: stats.total, cls: 'text-foreground' },
          { label: 'In Progress', value: stats.inProgress, cls: 'text-warning' },
          { label: 'Completed', value: stats.completed, cls: 'text-success' },
          { label: 'Pending Review', value: stats.pendingReview, cls: 'text-primary' },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-700 mt-1 ${s.cls}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, role…"
          className="flex-1 min-w-[200px] px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as OnboardingStatus | 'all')}
          className="px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="all">All Statuses</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="pending_review">Pending Review</option>
        </select>
        <select value={filterHireType} onChange={(e) => setFilterHireType(e.target.value as HireType | 'all')}
          className="px-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
          <option value="all">All Types</option>
          <option value="direct">Direct Hire</option>
          <option value="temp">Temp / Agency</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground text-sm">Loading onboarding records…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <UserPlus size={24} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-600 text-foreground">No onboarding records found</p>
          <p className="text-xs text-muted-foreground mt-1">Start a new hire onboarding to get started</p>
          <button onClick={() => setWizardOpen(true)}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 transition-colors">
            <Plus size={14} /> Start Onboarding
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((hire) => (
            <HireCard key={hire.id} hire={hire} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Wizard */}
      {wizardOpen && (
        <OnboardingWizard
          existing={editingHire}
          onClose={handleCloseWizard}
          onSaved={handleSaved}
        />
      )}
    </AppLayout>
  );
}
